import React, { useEffect } from "react";
import { useMemo, useState, useRef } from "react";
import InstitutionForm from "./InstitutionForm";
import { useNavigate, useOutletContext } from "react-router-dom";
import CommonTable from "../common/commonTable";
import "../../styles/admin/institution.scss";
import Visibility from "@mui/icons-material/Visibility";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Block from "@mui/icons-material/Block";
import CheckCircle from "@mui/icons-material/CheckCircle";
import GlobalLoader from "../common/GlobalLoader";
import TooltipText from "../common/tooltipText";
import {
  createInstitution,
  deleteInstitution,
  getAllInstitution,
  updateInstitution,
} from "../../helper/apicalls/institution";
import Cookies from "js-cookie";
import SuccessPopup from "../common/SuccessPopup";
import Pagination from "../common/Pagination";
import { getAllPreference } from "../../helper/apicalls/preference";
import { getBoards } from "../../helper/apicalls/course";

const Institution = ({}) => {
  const [institutions, setInstitutions] = useState([]);
  const [loader, setLoader] = useState(false);

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentInstitution, setCurrentInstitution] = useState(null);
  const [institutionToDelete, setInstitutionToDelete] = useState(null);
  const { setHeaderName, setIsShowSearch } = useOutletContext() || {};
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const authToken = Cookies.get("admin_token");
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState(
    "Institution created successfully"
  );
  const [popupType, setPopupType] = useState("success");
  const [preferenceList, setPreferenceList] = useState([]);
  const [boardOptions, setBoardOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ status: "", board: "" });

  const filteredInstitutions = useMemo(() => institutions, [institutions]);

  const searchDebounceRef = useRef(null);
  const handleSearchChange = (val) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(val);
    }, 300);
  };

  const formatCreatedDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  };

  const columns = useMemo(
    () => [
      { field: "name", label: "Name" },
      { field: "board", label: "Board" },
      {
        field: "description",
        label: "Description",
        render: (val) => (
          <TooltipText text={val || ""} className="cell-text" maxWidth={320} />
        ),
      },
      { field: "mobile", label: "Mobile Number" },
      {
        field: "disabled",
        label: "Status",
        align: "center",
        render: (isDisabled) => (
          <span
            className={`status-badge ${
              isDisabled ? "status-inactive" : "status-active"
            }`}
          >
            {isDisabled ? "Disabled" : "Active"}
          </span>
        ),
      },
      {
        field: "created_at",
        label: "Created",
        render: formatCreatedDate,
      },
    ],
    []
  );

  const handleEdit = (id, row) => {
    setCurrentInstitution(row);
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const res = await deleteInstitution(authToken, id);
      setSuccessMsg(res?.message || "Institution deleted successfully");
      setPopupType("success");
      setSuccessOpen(true);
      await fetchInstitutionData();
      await fetchInstitutionBoards();
    } catch (err) {
      console.error("Failed to delete institution:", err);
      setSuccessMsg(err?.message || "Failed to delete institution");
      setPopupType("error");
      setSuccessOpen(true);
    }
  };

  const toggleStatus = async (row) => {
    try {
      const updateResponse = await updateInstitution(
        { disabled: !row?.disabled },
        authToken,
        row?.id
      );
      setSuccessMsg(
        updateResponse?.message || "Institution status updated successfully"
      );
      setPopupType("success");
      setSuccessOpen(true);
      await fetchInstitutionData();
    } catch (err) {
      console.error("Failed to toggle status:", err);
      setSuccessMsg(err?.message || "Failed to update status");
      setPopupType("error");
      setSuccessOpen(true);
    }
  };

  const fetchInstitutionData = async () => {
    setLoader(true);
    try {
      const params = (() => {
        const p = {
          page,
          limit,
        };
        const s = (filters?.status || "").trim();
        const board = (filters?.board || "").trim();

        if (searchTerm.trim()) p.search = searchTerm.trim();
        if (board) p.board = board;
        if (s === "active") p.disabled = false;
        else if (s === "disabled") p.disabled = true;
        return p;
      })();
      const res = await getAllInstitution(authToken, params);
      setInstitutions(res?.data || []);
      const tp =
        Number(res?.total_pages) ||
        (Number(res?.total) && Math.ceil(Number(res.total) / limit)) ||
        1;
      setTotalPages(tp);
    } catch (err) {
      console.log(err);
    } finally {
      setLoader(false);
    }
  };

  const fetchInstitutionPreference = async () => {
    try {
      const res = await getAllPreference(authToken, {});
      setPreferenceList(res?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchInstitutionBoards = async () => {
    try {
      const res = await getBoards(null, { is_institution: true });
      const options = Array.isArray(res)
        ? res
            .map((board) => ({
              value: board?.name || "",
              label: board?.name || "",
            }))
            .filter((board) => board.value)
        : [];
      setBoardOptions(options);
    } catch (err) {
      console.error(err);
    }
  };

  const goToPage = (p) => {
    const next = Math.max(1, p);
    setPage(next);
  };

  const changePerPage = (pp) => {

    setLoader(true);
    setLimit(pp);
    setPage(1);
  };

  useEffect(() => {
    setHeaderName("Institutions");
    fetchInstitutionPreference();
    fetchInstitutionBoards();
    setIsShowSearch(false);
  }, []);

  useEffect(() => {
    fetchInstitutionData();
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm, filters, page, limit]);

  return (
    <div className="page-wrap">
      {loader && (
        <GlobalLoader visible={loader} text="Loading institution..." />
      )}

      {isFormOpen && (
        <InstitutionForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setCurrentInstitution(null);
          }}
          preferenceList={preferenceList}
          onSubmit={async (formData) => {
            if (currentInstitution) {
              try {
                const updateResponse = await updateInstitution(
                  formData,
                  authToken,
                  currentInstitution?.id
                );
                setSuccessMsg(
                  updateResponse?.message || "Institution updated successfully"
                );
                setPopupType("success");
                setSuccessOpen(true);
                await fetchInstitutionData();
                await fetchInstitutionBoards();
                setIsFormOpen(false);
                setCurrentInstitution(null);
              } catch (err) {
                console.error("Failed to update institution:", err);
                setSuccessMsg(err?.message || "Failed to update institution");
                setPopupType("error");
                setSuccessOpen(true);
              }
            } else {
              try {

                const res = await createInstitution(formData, authToken);
                setSuccessMsg(
                  res?.message || "Institution created successfully"
                );
                setPopupType("success");
                setSuccessOpen(true);
                await fetchInstitutionData();
                await fetchInstitutionBoards();
                setIsFormOpen(false);
                setCurrentInstitution(null);
              } catch (err) {
                console.error("Failed to create institution:", err);
                setSuccessMsg(err?.message || "Failed to create institution");
                setPopupType("error");
                setSuccessOpen(true);
              }
            }
          }}
          initialData={currentInstitution}
        />
      )}

      <CommonTable
        data={filteredInstitutions}
        columns={columns}
        getRowId={(r) => r.id}
        showToolbar
        onSearchChange={handleSearchChange}
        filterSections={[
          {
            key: "board",
            title: "Board",
            multiple: false,
            options: [{ value: "", label: "All" }, ...boardOptions],
          },
          {
            key: "status",
            title: "Status",
            multiple: false,
            options: [
              { value: "", label: "All" },
              { value: "active", label: "Active" },
              { value: "disabled", label: "Disabled" },
            ],
          },
        ]}
        filterValues={filters}
        onFilterChange={(nextFilters) => {
          setFilters({
            status: nextFilters?.status || "",
            board: nextFilters?.board || "",
          });
          setPage(1);
        }}
        actionLabelEdit="Edit"
        actionLabelDelete="Delete"
        extraActions={(row, rowKey) => {
          const items = [];
          if (row.disabled) {
            items.push({
              key: "enable",
              label: "Enable",
              icon: (
                <CheckCircle
                  fontSize="small"
                  sx={{ color: "var(--color-success)" }}
                />
              ),
              onClick: () => toggleStatus(row),
            });
          } else {
            items.push({
              key: "disable",
              label: "Disable",
              icon: <Block fontSize="small" />,
              onClick: () => toggleStatus(row),
              danger: true,
            });
          }

          return items;
        }}
        toolbarRight={
          <button
            className="institution-create-btn btn-interactive"
            onClick={() => {
              setCurrentInstitution(null);
              setIsFormOpen(true);
            }}
          >
            Create
          </button>
        }
        showActions
        onRowClick={(row) => {
          navigate(`/admin/institution/info?id=${row?.id}`, {
            state: { title: row?.name, type: row?.type },
          });
        }}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isConfirmDeleteOpen={isConfirmDeleteOpen}
        setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
        maxBodyHeight={`calc(100vh - 220px)`}
        fixedBodyRows={10}
        rowHeight={44}
      />
      <div className="teacher-pagination">
        <Pagination
          page={page}
          pageCount={totalPages}
          onChange={goToPage}
          perPage={limit}
          perPageOptions={[5, 10, 25, 100]}
          onPerPageChange={changePerPage}
        />
      </div>
     {successOpen&&  <SuccessPopup
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        message={successMsg}
        type={popupType}
      />}
    </div>
  );
};

export default Institution;
